import { useState } from 'react';
import {
  calcularDesdeNeto,
  calcularDesdeTotal,
  formatearCLP,
} from '@lexicore/shared';
import { IconoCerrar } from '../../componentes/Iconos';
import { useAvisos } from '../../contextos/AvisosContext';
import type { Cuota, ProyectoDetalle } from '../../lib/consultas';
import { useActualizarCuotaManual, useCrearCuotaManual } from '../../lib/mutaciones';

interface Props {
  proyecto: ProyectoDetalle;
  cuota?: Cuota | null;
  alCerrar: () => void;
  alGuardarExitoso?: () => void;
}

export function ModalCuotaManual({
  proyecto,
  cuota,
  alCerrar,
  alGuardarExitoso,
}: Props) {
  const avisos = useAvisos();
  const crearMut = useCrearCuotaManual();
  const actualizarMut = useActualizarCuotaManual();

  const esEdicion = !!cuota;

  const [descripcion, setDescripcion] = useState(cuota?.descripcion ?? '');
  const [fechaEmision, setFechaEmision] = useState(
    cuota ? cuota.fechaEmisionEstimada.slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [fechaCobro, setFechaCobro] = useState(
    cuota && cuota.fechaCobroEstimada ? cuota.fechaCobroEstimada.slice(0, 10) : '',
  );
  const [montoNeto, setMontoNeto] = useState<number>(cuota?.montoNeto ?? 0);
  const [montoTotal, setMontoTotal] = useState<number>(cuota?.montoTotal ?? 0);
  const [hitoId, setHitoId] = useState<string>(cuota?.hitoId ?? '');

  const cambiarNeto = (valorStr: string) => {
    const neto = Math.max(0, parseInt(valorStr.replace(/\D/g, ''), 10) || 0);
    setMontoNeto(neto);
    const calculo = calcularDesdeNeto(neto);
    setMontoTotal(calculo.montoTotal);
  };

  const cambiarTotal = (valorStr: string) => {
    const total = Math.max(0, parseInt(valorStr.replace(/\D/g, ''), 10) || 0);
    setMontoTotal(total);
    const calculo = calcularDesdeTotal(total);
    setMontoNeto(calculo.montoNeto);
  };

  const guardar = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!descripcion.trim()) {
      avisos.error('El concepto o descripción de la cuota es obligatorio.');
      return;
    }
    if (!fechaEmision) {
      avisos.error('La fecha de emisión estimada es obligatoria.');
      return;
    }
    if (montoNeto <= 0) {
      avisos.error('El monto neto debe ser mayor a $0.');
      return;
    }

    try {
      if (esEdicion && cuota) {
        await actualizarMut.mutateAsync({
          cuotaId: cuota.id,
          descripcion: descripcion.trim(),
          fechaEmisionEstimada: fechaEmision,
          fechaCobroEstimada: fechaCobro || undefined,
          montoNeto,
          montoTotal,
          hitoId: hitoId || null,
        });
        avisos.exito('Cuota actualizada correctamente.');
      } else {
        await crearMut.mutateAsync({
          proyectoId: proyecto.id,
          descripcion: descripcion.trim(),
          fechaEmisionEstimada: fechaEmision,
          fechaCobroEstimada: fechaCobro || undefined,
          montoNeto,
          montoTotal,
          hitoId: hitoId || null,
        });
        avisos.exito('Nueva cuota añadida al cronograma.');
      }

      if (alGuardarExitoso) alGuardarExitoso();
      alCerrar();
    } catch (err) {
      avisos.error(
        err instanceof Error ? err.message : 'No se pudo guardar la cuota.',
      );
    }
  };

  const enviando = crearMut.isPending || actualizarMut.isPending;

  return (
    <div
      className="modal-overlay"
      onClick={(e) => e.target === e.currentTarget && alCerrar()}
      role="dialog"
      aria-modal="true"
    >
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div>
            <h2>{esEdicion ? 'Editar Cuota de Facturación' : 'Añadir Cuota Manual'}</h2>
            <p style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 }}>
              Proyecto: <strong>{proyecto.codigo}</strong>
            </p>
          </div>
          <button
            type="button"
            className="btn-icono"
            onClick={alCerrar}
            aria-label="Cerrar modal"
          >
            <IconoCerrar size={14} />
          </button>
        </div>

        <form onSubmit={guardar}>
          <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div className="form-group">
              <label htmlFor="inputDescripcionCuota">Concepto / Descripción de la Cuota *</label>
              <input
                id="inputDescripcionCuota"
                type="text"
                value={descripcion}
                onChange={(e) => setDescripcion(e.target.value)}
                placeholder="Ej: Cuota 05/12 - Soporte mensual o Hito Fase 1"
                required
                autoFocus
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label htmlFor="inputFechaEmision">Fecha Emisión Estimada *</label>
                <input
                  id="inputFechaEmision"
                  type="date"
                  value={fechaEmision}
                  onChange={(e) => setFechaEmision(e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="inputFechaCobro">Fecha Cobro Estimada</label>
                <input
                  id="inputFechaCobro"
                  type="date"
                  value={fechaCobro}
                  onChange={(e) => setFechaCobro(e.target.value)}
                  placeholder="Opcional"
                />
              </div>
            </div>

            {/* Montos y cálculo automático de IVA al 19% */}
            <div
              style={{
                background: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
                borderRadius: 'var(--radius-sm)',
                padding: '14px 16px',
                display: 'flex',
                flexDirection: 'column',
                gap: 12,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-primary)' }}>
                Importe de la Cuota (Cálculo automático de IVA 19%)
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label htmlFor="inputMontoNeto">Monto Neto ($ CLP)</label>
                  <input
                    id="inputMontoNeto"
                    type="text"
                    value={montoNeto > 0 ? montoNeto.toLocaleString('es-CL') : ''}
                    onChange={(e) => cambiarNeto(e.target.value)}
                    placeholder="0"
                    style={{ fontFamily: 'var(--font-mono)', fontWeight: 600 }}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="inputMontoTotal">Monto Total Bruto (con IVA)</label>
                  <input
                    id="inputMontoTotal"
                    type="text"
                    value={montoTotal > 0 ? montoTotal.toLocaleString('es-CL') : ''}
                    onChange={(e) => cambiarTotal(e.target.value)}
                    placeholder="0"
                    style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--accent-blue)' }}
                  />
                </div>
              </div>

              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                IVA estimado (19%): <strong style={{ fontFamily: 'var(--font-mono)' }}>{formatearCLP(montoTotal - montoNeto)}</strong>
              </div>
            </div>

            {proyecto.hitos.length > 0 && (
              <div className="form-group">
                <label htmlFor="selectHitoVinculado">Vincular a Hito Técnico (Opcional)</label>
                <select
                  id="selectHitoVinculado"
                  value={hitoId}
                  onChange={(e) => setHitoId(e.target.value)}
                  style={{ width: '100%' }}
                >
                  <option value="">Sin hito vinculado</option>
                  {proyecto.hitos.map((h) => (
                    <option key={h.id} value={h.id}>
                      {h.nombre} {h.completado ? '(Completado)' : '(Pendiente)'}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-secondary"
              onClick={alCerrar}
              disabled={enviando}
            >
              Cancelar
            </button>
            <button
              type="submit"
              className="btn btn-primary"
              disabled={enviando}
            >
              {enviando ? 'Guardando…' : esEdicion ? 'Guardar Cambios' : 'Añadir Cuota'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
